/**
 * Session-experience helpers, ported from the reference's
 * lib/experience-profile.ts + lib/experience-branding.ts (b80b4874).
 *
 * IMPORTANT (#901, ported rule): the wizard themes and brands from the
 * SESSION's experience profile only — the snapshot pinned on the session
 * row, or (for a fresh wizard) the profile the mounting page passed in.
 * There is NO fallback to the viewing user's profile: that leaked an
 * unrelated viewer's vendor branding onto sessions that aren't theirs.
 * A null profile is the default PSCX VIP look.
 */
import type { CSSProperties } from "react";

export interface ExperienceProfileBrand {
  name: string;
  logo_url: string;
  favicon_url?: string;
  accent_color: string;
  accent_soft_color: string;
  primary_action_color: string;
  primary_action_hover_color: string;
  show_powered_by?: boolean;
}

export interface ExperienceProfileCopy {
  header_eyebrow?: string;
  sidebar_label?: string;
  project_type_title?: string;
  project_type_subtitle?: string;
  source_title?: string;
  source_subtitle?: string;
  deployment_title?: string;
  deployment_subtitle?: string;
  review_title?: string;
  review_subtitle?: string;
  primary_cta_label?: string;
  sow_cta_label?: string;
}

export interface ExperienceProfile {
  key: string;
  name: string;
  mode?: string;
  locked_destination_vendor_key?: string;
  locked_destination_vendor_name?: string;
  locked_deployment_type?: string;
  hidden_steps?: string[];
  disabled_features?: string[];
  /** Live org/platform scheduling knob (#315) — merged over the session
   * snapshot at resume, never frozen per session. */
  scheduling?: { enabled?: boolean; scheduler_link?: string } | null;
  brand: ExperienceProfileBrand;
  copy: ExperienceProfileCopy;
}

export function hasDisabledFeature(
  profile: ExperienceProfile | null | undefined,
  feature: string,
): boolean {
  return profile?.disabled_features?.includes(feature) ?? false;
}

export function disabledFeatures(
  profile: ExperienceProfile | null | undefined,
): string[] {
  return profile?.disabled_features ?? [];
}

/**
 * Wizard steps to drop for this profile: the admin-configured hidden_steps
 * plus one derived entry — a locked destination vendor removes the
 * Destination Platform step (#1223: the choice does not exist; the provider
 * overwrites any pick and the backend rejects a conflicting one). Derived
 * HERE so every consumer of buildWizardSteps agrees on the step list.
 */
export function hiddenSteps(
  profile: ExperienceProfile | null | undefined,
): string[] {
  const hidden = profile?.hidden_steps ?? [];
  if (!profile?.locked_destination_vendor_key) return hidden;
  // Preserve array identity when nothing is added — callers memoise on it.
  return hidden.includes("destination_vendor")
    ? hidden
    : [...hidden, "destination_vendor"];
}

/** #rgb / #rrggbb only — anything else is not a colour we will inject
 * (same rule as brandTheme.ts). */
const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

type ExperienceStyle = CSSProperties & Record<`--${string}`, string>;

/**
 * Inline CSS-var overrides for a branded session. The five vars are the
 * SAME token names this app's globals.css defines (the FN tokens were
 * derived from the reference's — see globals.css header), so the override
 * scopes cleanly to the wizard subtree. undefined = default theme.
 */
export function experienceThemeStyle(
  profile: ExperienceProfile | null | undefined,
): ExperienceStyle | undefined {
  if (!profile) return undefined;
  // A profile with no accent configured legitimately serves "" (#484: the
  // VIP surface carries NO single-colour override, because VIP's brand is
  // two colours and the built-in `data-brand="vip"` palette already IS
  // them). Injecting "" into a custom property does not fall back — it
  // blanks every colour derived from it — so leave the palette alone,
  // exactly as `brandThemeStyle` does for the shell.
  if (!HEX.test(profile.brand.accent_color?.trim() ?? "")) return undefined;
  return {
    "--c-accent": profile.brand.accent_color,
    "--c-accent-hover": profile.brand.primary_action_hover_color,
    "--c-accent-cta": profile.brand.primary_action_color,
    "--c-accent-cta-hover": profile.brand.primary_action_hover_color,
    "--c-blue-pale": profile.brand.accent_soft_color,
  };
}

export function brandName(profile: ExperienceProfile | null | undefined): string {
  return profile?.brand.name?.trim() || "PSCX VIP";
}

/**
 * The work-item noun, ported verbatim from the reference's
 * `lib/experience-branding.ts`: a branded ORG says "Order(s)", an
 * unbranded one says "Project(s)".
 *
 * THE INPUT IS THE ORG'S PROFILE, NEVER THE SERVED SURFACE. Those are
 * different questions — the surface says WHICH APP you are on, the profile
 * says whether YOUR COMPANY is branded — and inside VIP both branded and
 * unbranded orgs exist. Deriving this noun from the surface hands "Orders"
 * to unbranded VIP orgs that saw "Projects" before the migration, which is
 * exactly the tell the consolidation is not allowed to produce. Ruled
 * 2026-08-18 after the two apps were diffed side by side; the four screens
 * that had drifted onto the surface were moved back here.
 *
 * The consolidated backend populates `experience_profile` as of contract
 * v2.9 (slice 5, PR #342): AuthenticationService.get_session_user resolves
 * override -> own-org binding -> host and spine_api renders the measured
 * VIP wire shape. Branded orgs get their noun back here with no frontend
 * change; unbranded users still resolve null — exact parity either way.
 */
export function projectNoun(
  profile: ExperienceProfile | null | undefined,
  plural = false,
): string {
  if (!profile) return plural ? "Projects" : "Project";
  return plural ? "Orders" : "Order";
}

/** Portal heading — the org's brand name, or "Dashboard" unbranded. */
export function portalTitle(profile: ExperienceProfile | null | undefined): string {
  return profile ? brandName(profile) : "Dashboard";
}

/** Portal strapline; same profile key as {@link projectNoun}. */
export function portalSubtitle(profile: ExperienceProfile | null | undefined): string {
  return profile
    ? "Create, track, and manage customer migration orders."
    : "Win work, deliver faster, and grow services revenue.";
}

/** Primary CTA label; same profile key as {@link projectNoun}. */
export function startOrderLabel(profile: ExperienceProfile | null | undefined): string {
  return profile ? "Start New Order" : "Start New Quote";
}

export function brandLogo(profile: ExperienceProfile | null | undefined): string {
  return profile?.brand.logo_url?.trim() || "/pscx-vip-logo.svg";
}

/** The PSCX VIP mark used by the "powered by" attribution. */
export const POWERED_BY_LOGO = "/pscx-vip-logo.svg";

/** Attribution shows only when the org actually has a custom logo on display
 * (without one {@link brandLogo} falls back to the PSCX mark, so the
 * attribution would sit under the PSCX logo itself — VIP #1383), and only
 * when the profile's white-label setting allows it. Mirrors brandLogo's own
 * custom-logo predicate so the two stay in step. */
export function showPoweredBy(profile: ExperienceProfile | null | undefined): boolean {
  return Boolean(profile?.brand.logo_url?.trim()) && profile?.brand.show_powered_by !== false;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

/** Parse an untyped experience_profile payload (the auth user's, or a
 * session's snapshot) into the typed shape, or null. */
export function parseExperienceProfile(value: unknown): ExperienceProfile | null {
  if (!isRecord(value)) return null;
  if (!isRecord(value.brand) || !isRecord(value.copy)) return null;
  return value as unknown as ExperienceProfile;
}
